import React from "react";
import { Row, Col, Badge, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import { DateFormatter } from "../../utilityFunctions/utilsFunctions";
import styles from "./VendorDocumentsSection.module.scss";

function VendorDocumentsSection({
  cafe,
  dateFormat,
  canReviewDocuments,
  onReviewDocument,
}) {
  if (!cafe) {
    return null;
  }

  const hasAnyDocument =
    cafe.healthPermitUrl ||
    cafe.insuranceUrl ||
    cafe.insuranceSkipped ||
    cafe.ownershipDocumentUrl;

  if (!hasAnyDocument) {
    return null;
  }

  const reviewBadge = (status, { skipped } = {}) => {
    if (skipped) {
      return (
        <Badge bg="info">
          <i className="fa fa-clock-o me-1" aria-hidden="true" />
          Skipped
        </Badge>
      );
    }
    if (status === "pending_review") {
      return (
        <Badge bg="warning">
          <i className="fa fa-exclamation-circle me-1" aria-hidden="true" />
          Needs Review
        </Badge>
      );
    }
    if (status === "reviewed") {
      return (
        <Badge bg="success">
          <i className="fa fa-check-circle me-1" aria-hidden="true" />
          Reviewed
        </Badge>
      );
    }
    if (status === "rejected") {
      return (
        <Badge bg="danger">
          <i className="fa fa-times-circle me-1" aria-hidden="true" />
          Rejected
        </Badge>
      );
    }
    return null;
  };

  const viewDocumentLink = (url) => (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn-sm btn-primary"
    >
      <i className="fa fa-eye me-1" aria-hidden="true" />
      View Document
    </a>
  );

  const markReviewedButton = (documentType) =>
    canReviewDocuments ? (
      <Button
        size="sm"
        variant="success"
        className="ms-2"
        onClick={() => onReviewDocument(documentType)}
      >
        <i className="fa fa-check me-1" aria-hidden="true" />
        Mark as Reviewed
      </Button>
    ) : null;

  return (
    <Row className="mt-4 mb-2">
      <Col lg={12}>
        <div className={styles.sectionCard}>
          <h5 className={styles.sectionTitle}>
            <i className="fa fa-file-text-o me-2" aria-hidden="true" />
            Vendor Documents
          </h5>
          <Row className="mt-3 g-3">
            {cafe.healthPermitUrl && (
              <Col md={6}>
                <div className={styles.documentItem}>
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <span className={styles.documentLabel}>Health Permit</span>
                    {reviewBadge(cafe.healthPermitReviewStatus)}
                  </div>
                  <div className="d-flex flex-wrap align-items-center">
                    {viewDocumentLink(cafe.healthPermitUrl)}
                    {cafe.healthPermitReviewStatus === "pending_review" &&
                      markReviewedButton("healthPermit")}
                  </div>
                  {cafe.healthPermitUploadedAt && (
                    <small className="d-block text-muted mt-2">
                      Uploaded:{" "}
                      {DateFormatter(cafe.healthPermitUploadedAt, dateFormat)}
                    </small>
                  )}
                  {cafe.healthPermitReviewedAt && (
                    <small className="d-block text-muted">
                      Reviewed:{" "}
                      {DateFormatter(cafe.healthPermitReviewedAt, dateFormat)}
                    </small>
                  )}
                </div>
              </Col>
            )}

            {(cafe.insuranceUrl || cafe.insuranceSkipped) && (
              <Col md={6}>
                <div className={styles.documentItem}>
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <span className={styles.documentLabel}>Insurance</span>
                    {reviewBadge(cafe.insuranceReviewStatus, {
                      skipped: cafe.insuranceSkipped,
                    })}
                  </div>
                  {cafe.insuranceSkipped ? (
                    <>
                      <p className="text-muted small mb-2">
                        <i className="fa fa-info-circle me-1" aria-hidden="true" />
                        Vendor skipped insurance upload
                      </p>
                      {cafe.insuranceDueDate && (
                        <small className="text-danger d-block mt-2">
                          Due by:{" "}
                          {DateFormatter(cafe.insuranceDueDate, dateFormat)}
                        </small>
                      )}
                    </>
                  ) : (
                    cafe.insuranceUrl && (
                      <>
                        <div className="d-flex flex-wrap align-items-center">
                          {viewDocumentLink(cafe.insuranceUrl)}
                          {cafe.insuranceReviewStatus === "pending_review" &&
                            markReviewedButton("insurance")}
                        </div>
                        {cafe.insuranceUploadedAt && (
                          <small className="d-block text-muted mt-2">
                            Uploaded:{" "}
                            {DateFormatter(cafe.insuranceUploadedAt, dateFormat)}
                          </small>
                        )}
                        {cafe.insuranceReviewedAt && (
                          <small className="d-block text-muted">
                            Reviewed:{" "}
                            {DateFormatter(cafe.insuranceReviewedAt, dateFormat)}
                          </small>
                        )}
                      </>
                    )
                  )}
                </div>
              </Col>
            )}

            {cafe.ownershipDocumentUrl && (
              <Col md={6}>
                <div className={styles.documentItem}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className={styles.documentLabel}>
                      Ownership Document
                    </span>
                  </div>
                  {viewDocumentLink(cafe.ownershipDocumentUrl)}
                </div>
              </Col>
            )}

          </Row>
        </div>
      </Col>
    </Row>
  );
}

VendorDocumentsSection.propTypes = {
  cafe: PropTypes.object,
  dateFormat: PropTypes.string,
  canReviewDocuments: PropTypes.bool,
  onReviewDocument: PropTypes.func,
};

VendorDocumentsSection.defaultProps = {
  cafe: null,
  dateFormat: "MonthDayYear",
  canReviewDocuments: false,
  onReviewDocument: () => {},
};

export default VendorDocumentsSection;
